import { useCallback, useRef, useState } from 'react';

interface FocusPointOverlayProps {
  focusX: number;
  focusY: number;
  onFocusChange: (x: number, y: number) => void;
}

function getVisibleImageRect(container: HTMLElement): DOMRect | null {
  const containerRect = container.getBoundingClientRect();
  if (containerRect.width <= 0 || containerRect.height <= 0) {
    return null;
  }

  const img = container.parentElement?.querySelector('img');
  if (!img) {
    return containerRect;
  }
  if (!img.naturalWidth || !img.naturalHeight) {
    return null;
  }

  const fit = getComputedStyle(img).objectFit;

  if (fit === 'cover') {
    return containerRect;
  }

  const imgAspect = img.naturalWidth / img.naturalHeight;
  const containerAspect = containerRect.width / containerRect.height;

  let w: number;
  let h: number;
  let ox: number;
  let oy: number;
  if (imgAspect > containerAspect) {
    w = containerRect.width;
    h = containerRect.width / imgAspect;
    ox = 0;
    oy = (containerRect.height - h) / 2;
  } else {
    h = containerRect.height;
    w = containerRect.height * imgAspect;
    ox = (containerRect.width - w) / 2;
    oy = 0;
  }

  return new DOMRect(containerRect.x + ox, containerRect.y + oy, w, h);
}

function pointerToPercent(e: { clientX: number; clientY: number }, rect: DOMRect) {
  const x = Math.round(Math.max(0, Math.min(100, ((e.clientX - rect.x) / rect.width) * 100)));
  const y = Math.round(Math.max(0, Math.min(100, ((e.clientY - rect.y) / rect.height) * 100)));
  return { x, y };
}

export function FocusPointOverlay({ focusX, focusY, onFocusChange }: FocusPointOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const imgRectRef = useRef<DOMRect | null>(null);

  const computeAndApply = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const rect = imgRectRef.current;
      if (!rect) return;
      const { x, y } = pointerToPercent(e, rect);
      onFocusChange(x, y);
    },
    [onFocusChange],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!containerRef.current) return;
      imgRectRef.current = getVisibleImageRect(containerRef.current);
      if (!imgRectRef.current) return;
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setIsDragging(true);
      computeAndApply(e);
    },
    [computeAndApply],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      computeAndApply(e);
    },
    [isDragging, computeAndApply],
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    imgRectRef.current = null;
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 cursor-crosshair"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <div
        className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-white/30 ring-4 ring-black/40 shadow-lg"
        style={{ left: `${focusX}%`, top: `${focusY}%` }}
      />
    </div>
  );
}
